import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { get, merge } from 'lodash';
import {
  TestSetupComponent,
  ITestSetupEntry,
  BaseSetupTab,
  TestSetupTabsComponent,
  SaveTemplateComponent,
  TestSetupModule,
  TemplateService
} from '@nrwl/test-setup';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import {
  DialogService,
  HelpersService,
  NotificationService,
  TestListService,
  TestMode,
  TestTypeValue,
  ValuesExchangeProvider
} from '@nrwl/common-lib';
import { UdpSetupService } from './udp-setup.service';
import { TemplateTabsComponent } from '@nrwl/template-management';
import { UdpSetupComponent } from './tabs/udp-setup/udp-setup.component';
import { UdpStreamsComponent } from './tabs/udp-streams/udp-streams.component';
import { UDPConfigFieldMapping as ConfigFieldMapping } from './models/models';

@Component({
  selector: 'nrwl-y1731-setup',
  standalone: true,
  imports: [TestSetupModule],
  providers: [UdpSetupService],
  template: '<nrwl-test-setup-tabs [parentObj]="currentObj"></nrwl-test-setup-tabs>'
})
export class UdpTestSetupComponent
  extends TestSetupComponent
  implements OnInit, AfterViewInit, OnDestroy, ITestSetupEntry
{
  @ViewChild(TestSetupTabsComponent, { static: true }) testSetupTabRef: TestSetupTabsComponent;
  templateDialogRef: MatDialogRef<TemplateTabsComponent>;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  setupConfig: any;
  agentBasedConfig: any;
  setupId: string;
  mode: string;
  templateName: string;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose = new EventEmitter<any>();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataId: any;
  router: Router;
  setupService: UdpSetupService;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  openWizard: any;
  subscription: Subscription;
  changeDetectorRef: ChangeDetectorRef;
  activatedRouteSubscription: Subscription;
  editDeviceDialogRef: MatDialogRef<SaveTemplateComponent>;
  currentObj: any; //eslint-disable-line
  templateId: string;
  testId = null;
  shaId: string;
  constructor(
    router: Router,
    setupService: UdpSetupService,
    changeDetectorRef: ChangeDetectorRef,
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigationProvider: NavigationProvider,
    private readonly testListService: TestListService,
    private readonly exchange: ValuesExchangeProvider,
    private readonly notificationService: NotificationService,
    private helpersService: HelpersService,
    private readonly templateService: TemplateService,
    private dialog: MatDialog,
    private readonly dialogService: DialogService
  ) {
    super();
    this.router = router;
    this.setupService = setupService;
    this.changeDetectorRef = changeDetectorRef;
  }

  ngOnInit() {
    this.currentObj = this;
    if (this.mode === 'new') {
      this.setupService.getControllerConfiguration();
    }
    this.activatedRouteSubscription = this.activatedRoute.queryParams.subscribe(data => {
      if (this.testId !== this.setupId) {
        const defaultParams = merge(
          {},
          this.dataId,
          { modeType: this.mode },
          JSON.parse(get(data, 'defaultParams', '{}'))
        );
        this.templateId = defaultParams?.templateId;
        this.setupService.openTestSetup(
          this.setupId,
          this.setupConfig,
          defaultParams,
          null,
          null,
          this.agentBasedConfig
        );
        this.testId = this.setupId;
      }
    });
    this.getLicenses();
    this.getServerConfig();
    if (!this.setupService.editMode) {
      this.setupService.currentConfig.buttonName = 'START TEST';
    }
    this.templateService.closeDialog?.subscribe(status => {
      if (status && this.templateDialogRef) {
        this.templateDialogRef.close();
      }
    });
  }

  getLicenses(): void {
    this.setupService.getLicenseData().subscribe(response => {
      this.setupService.clientToAgentInfo.licenseInfo = response;
    });
  }

  getServerConfig(): void {
    this.setupService.getServerConfig().subscribe(response => {
      this.setupService.clientToAgentInfo.serverConfigInfo = response;
      if (
        this.setupService.editMode &&
        this.setupService.currentTab.output.testMode === TestMode.CLIENT_TO_MA
      ) {
        this.shaId = this.setupService.tabs[0].output['shaId'];
        this.setClientURLs();
      }
    });
  }

  setClientURLs(): void {
    this.setupService.clientToAgentInfo.secondaryTestURL = null;
    this.setupService.clientToAgentInfo.dmzServerUrl = null;
    const dmzUrl = this.helpersService.createPublicAccessURL(
      this.setupService.clientToAgentInfo.serverConfigInfo.data,
      this.shaId
    );
    const vTAUrl = this.setupService.generateSecondaryTestURL(
      this.setupService.clientToAgentInfo.serverConfigInfo.data,
      this.shaId
    );
    if (vTAUrl) {
      this.setupService.clientToAgentInfo.secondaryTestURL = `${vTAUrl}&type=udp`;
    }
    if (dmzUrl) {
      this.setupService.clientToAgentInfo.dmzServerUrl = `${dmzUrl}&type=udp`;
    }
  }
  // eslint-disable-next-line
  generateUrls(response: any): void {
    if (response && response.status === 'success') {
      this.setupService.editMode = true;
      if (response.data && response.data.sha) {
        this.shaId = response.data.sha;
        this.setupService.tabs[0].output['shaId'] = this.shaId;
        this.setClientURLs();
        if (
          this.setupService.clientToAgentInfo.secondaryTestURL !== null ||
          this.setupService.clientToAgentInfo.dmzServerUrl !== null
        ) {
          this.notificationService.onSuccess(
            "Test has been added.To start test,copy a URL and paste inside the PC's browser"
          );
          this.setupService.currentConfig.buttonName = 'UPDATE';
        }
        if (
          this.setupService.clientToAgentInfo.secondaryTestURL === null &&
          this.setupService.clientToAgentInfo.dmzServerUrl === null
        ) {
          this.notificationService.onSuccess(
            'The test was successfully created. However, The test URLs were not generated because the chosen agent does not have public access enabled, or a public access server was not set under Settings->Services.'
          );
        }
      } else {
        this.notificationService.onSuccess(
          'Test created successfully but SHA not found in the response'
        );
      }
    }
  }

  ngAfterViewInit() {
    this.createContentComponent(1);
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.activatedRouteSubscription) {
      this.activatedRouteSubscription.unsubscribe();
    }
  }
  // eslint-disable-next-line
  get(object: any, path: string): any {
    return get(object, path);
  }

  //-------------- Control && Validation -----------------------
  toggleTabChange(index: number): void {
    this.setupService.toggleTabChange(index);
    const tabIndex = index + 1;
    // create the component only once when its not available
    if (!this.testSetupTabRef['contentRef' + `${tabIndex}`].length) {
      this.createContentComponent(tabIndex);
    }
  }

  toggleClose(): void {
    this.setupService.toggleClose(() => this.onClose.emit(true));
  }

  toggleReOpen(): void {
    this.setupService.reOpen();
  }

  toggleFinish(): void {
    this.createUdpTest();
  }

  createUdpTest(): void {
    this.setupService.onNext();
    const allTestParams = this.setupService.getAllTestParams();
    this.setupService.openTestStatusDialog(
      this.setupService.editMode ? 'Updating udp' : 'Creating udp',
      this.setupService.editMode ? 'Update' : 'Add'
    );
    const lmapObject = this.setupService.createLMAPObject(allTestParams);
    if (this.setupService.output.testMode === TestMode.CLIENT_TO_MA) {
      if (this.setupService.editMode) {
        lmapObject['runlater'] = 'yes';
        this.subscription = this.setupService
          ._updateControllerTest({
            opt: { id: this.shaId, apply: 'next-run' },
            config: lmapObject
          })
          .subscribe(
            response => {
              if (response.status === 'success') {
                this.notificationService.onSuccess('Configuration was updated successfully');
                this.setupService.dialogRef.close();
                this.testSetupTabRef.toggleTabChange(0);
              }
            },
            err => {
              this.setupService.dialogRef.close();
              this.notificationService.onFail('Configuration was not updated successfully');
            }
          );
      } else {
        this.subscription = this.setupService._createControllerTest(lmapObject).subscribe(
          response => {
            this.testListService.updateTestResponse();
            this.generateUrls(response);
            this.setupService.dialogRef.close();
            this.exchange.notifyListeners('new_test', response);
            this.testSetupTabRef.toggleTabChange(0);
          },
          error => {
            const message = this.setupService.editMode
              ? 'Failed to update udp test'
              : 'Failed to create udp test';
            this.notificationService.onFail(message);
            this.setupService.dialogRef.close();
          }
        );
      }
    } else {
      if (!this.setupService.editMode) {
        this.subscription = this.setupService._saveControllerTest(lmapObject).subscribe(
          response => {
            const message = 'UDP test has been created';
            this.notificationService.onSuccess(message);
            this.testListService.updateTestResponse();
            this.setupService.dialogRef.close();
            this.toggleClose();
            this.exchange.notifyListeners('new_test', response);
            this.openTestRunning(response.data.id);
          },
          error => {
            const message = 'Failed to create UDP test';
            this.notificationService.onFail(message);
            this.setupService.dialogRef.close();
            this.toggleClose();
          }
        );
      } else {
        this.subscription = this.setupService
          ._updateControllerTest({
            opt: { id: this.setupService.defaultParamsById['scheduleId'], apply: 'next-run' },
            config: lmapObject
          })
          .subscribe(
            response => {
              const message = 'UDP test has been updated';
              this.notificationService.onSuccess(message);
              this.testListService.updateTestResponse();
              this.setupService.dialogRef.close();
              this.toggleClose();
              this.exchange.notifyListeners('new_test', response);
            },
            error => {
              const message = 'Failed to update UDP test';
              this.notificationService.onFail(message);
              this.setupService.dialogRef.close();
              this.toggleClose();
            }
          );
      }
    }
  }

  openTestRunning(testId: string): void {
    this.navigationProvider.navigateToDashboard(['test-running'], {
      queryParams: {
        testId: testId,
        testName: this.setupService.output.alias,
        testType: TestTypeValue.UDP,
        source: null,
        layeredSource: [],
        queryParamsHandling: 'merge',
        replaceUrl: true,
        hideTestSetup: true // to hide the testsetup wizard panel
      }
    });
  }

  downloadTestConfig(): void {
    this.setupService.onNext();
    const allTestParams = this.setupService.getAllTestParams();
    const lmapObject = this.setupService.createLMAPObject(allTestParams);
    this.setupService._downloadTestConfig(lmapObject);
  }

  saveTestConfig(): void {
    this.templateService.setTemplateId(this.templateId);
    this.setupService.onNext();
    const params = this.setupService.getAllTestParams();
    let configParams = {};
    configParams['udp-parameters'] = params?.['udp-parameters'];
    const otherParams = {
      [ConfigFieldMapping.SCHEDULER]: this.setupService.output[ConfigFieldMapping.SCHEDULER]
    };
    configParams = { ...configParams, ...otherParams };
    const selectedOriginator =
      this.setupService.selectedOriginator ||
      this.setupService.output[ConfigFieldMapping.SELECTED_ORIGINATOR];
    const selectedDestination =
      this.setupService.selectedDestination ||
      this.setupService.output[ConfigFieldMapping.SELECTED_DESTINATION];
    const allTestParams = this.setupService._createTemplateFormat(
      configParams,
      selectedOriginator,
      selectedDestination
    );
    allTestParams['extended-config'] = this.setupService.getExtendedPlugingConfig();
    allTestParams['extended-config'].TestDuration =
      this.setupService.output[ConfigFieldMapping.TEST_DURATION];
    allTestParams['controller'] = {};
    allTestParams['controller']['max-exec-count'] =
      this.setupService.output[ConfigFieldMapping.MAX_EXECUTION_COUNTS];
    allTestParams['controller']['expiration-date'] =
      this.setupService.output[ConfigFieldMapping.EXPIRATION_DATE];
    this.editDeviceDialogRef = this.dialog.open(SaveTemplateComponent, {
      width: '70rem',
      disableClose: true,
      data: { deviceInfo: allTestParams, editMode: true, testType: TestTypeValue.UDP }
    });
  }

  openTemplates(): void {
    const param = {
      id: 'setupService.test_templates',
      callBackService: this,
      callBackFuntion: this.openTemplatesDialog
    };
    this.dialogService.showDialog('confirm', 'body', param);
  }
  // eslint-disable-next-line
  openTemplatesDialog(service: any): void {
    service.toggleClose();
    service.templateDialogRef = service.dialog.open(TemplateTabsComponent, {
      width: '100rem',
      minHeight: '50rem',
      height: 'calc(100vh - 20px)',
      disableClose: true,
      data: {
        filterType: TestTypeValue.UDP,
        myTemplatesColumnHeaders: ['name', 'accessLevel', 'lastModified', 'selectActions'],
        sharedTemplatesColumnHeaders: [
          'name',
          'accessLevel',
          'owner',
          'lastModified',
          'selectActions'
        ]
      }
    });
  }

  getSetupService(): UdpSetupService {
    return this.setupService;
  }
  getSetupTab(): BaseSetupTab {
    return this.setupService.currentTab;
  }

  getContentViewRef(index: number): ViewContainerRef {
    return this.testSetupTabRef['contentRef' + `${index}`];
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveComponentType(pageId: string): any {
    switch (pageId) {
      case 'udp.setup.html':
        return UdpSetupComponent;
      case 'udp.streams.html':
        return UdpStreamsComponent;
      default: {
        console.error('Unrecognized template name:', pageId);
        return null;
      }
    }
  }
}
